// Dashboard Main Entry Point

import { api } from './api';
import { appState, sessionsState, noProductsState, alternativesState, productsState, categoriesState, outOfStockState, ordersState, resetPaginatedState, updatePaginatedState } from './state';
import { formatDateTime, formatPrice, parseEuroDate, setText, setHTML, $, generateCSV, downloadFile } from './utils';
import { renderDurationChart, renderMessagesChart, renderDurationTimelineChart, renderProductBubbleChart, renderTrafficSourcesChart, renderDeviceTypeChart, renderFunnelChart } from './charts';
import { connectLiveStream, disconnectLiveStream, renderLiveSessions, renderLiveEventFeed, renderLiveFunnel } from './live-view';
import { loadSessionExplorer, initSessionExplorer } from './session-explorer';

// Initialize date pickers
function initDatePickers(): void {
  const flatpickr = (window as any).flatpickr;
  if (!flatpickr) return;

  const config = {
    dateFormat: 'd.m.Y',
    locale: 'de'
  };

  // Session Intelligence (top-level filters)
  flatpickr('#sessionIntelStartDate', config);
  flatpickr('#sessionIntelEndDate', config);

  // Sessions
  flatpickr('#sessionsStartDate', config);
  flatpickr('#sessionsEndDate', config);
  
  // No products
  flatpickr('#noProductsStartDate', config);
  flatpickr('#noProductsEndDate', config);
  
  // Alternatives
  flatpickr('#alternativesStartDate', config);
  flatpickr('#alternativesEndDate', config);
  
  // Products
  flatpickr('#productsStartDate', config);
  flatpickr('#productsEndDate', config);
  
  // Orders
  flatpickr('#ordersStartDate', config);
  flatpickr('#ordersEndDate', config);
}

// Tab switching
export function switchTab(tabName: string): void {
  appState.currentTab = tabName;

  // Update tab buttons
  document.querySelectorAll('.tab').forEach(tab => tab.classList.remove('active'));
  document.querySelectorAll('.tab-content').forEach(content => content.classList.remove('active'));

  // Show selected tab
  const tabContent = document.getElementById(`${tabName}-tab`);
  if (tabContent) {
    tabContent.classList.add('active');
  }

  // Highlight active tab button
  document.querySelectorAll('.tab').forEach(btn => {
    const text = btn.textContent?.toLowerCase() || '';
    if (text.includes(tabName.toLowerCase()) || 
        (tabName === 'live' && text.includes('live')) ||
        (tabName === 'explorer' && text.includes('explorer'))) {
      btn.classList.add('active');
    }
  });

  // Update refresh status text
  const refreshStatus = $('refreshStatus');
  if (refreshStatus) {
    refreshStatus.textContent = tabName === 'live' ? 'Real-time via SSE' : 'Auto-refresh every 30s';
  }

  // Connect/disconnect live stream
  if (tabName === 'live') {
    connectLiveStream();
  }
}

// Load clients dropdown
async function loadClients(): Promise<void> {
  try {
    // Get server config for default client ID
    const config = await api.getConfig();
    const defaultClientId = config.clientId || '';
    
    const data = await api.getClients();
    const select = $('clientFilter') as HTMLSelectElement;
    if (!select) return;
    
    const currentValue = select.value;
    select.innerHTML = '<option value="">All Clients</option>';
    
    data.clients.forEach(clientId => {
      const option = document.createElement('option');
      option.value = clientId;
      option.textContent = clientId;
      select.appendChild(option);
    });
    
    // Auto-select: server config default → current value → first client
    if (defaultClientId && data.clients.includes(defaultClientId)) {
      select.value = defaultClientId;
      appState.selectedClientId = defaultClientId;
    } else if (currentValue && data.clients.includes(currentValue)) {
      select.value = currentValue;
    } else if (data.clients.length > 0) {
      select.value = data.clients[0];
      appState.selectedClientId = data.clients[0];
    }
  } catch (error) {
    console.error('Error loading clients:', error);
  }
}

// Main data loading function (with optional date filters for Session Intelligence)
export async function loadData(startDate?: string, endDate?: string): Promise<void> {
  const clientId = appState.selectedClientId;
  
  try {
    // Load session metrics with optional date filters
    const metricsData = await api.getSessionMetrics(clientId, startDate, endDate ? endDate + 'T23:59:59' : undefined);
    setText('totalSessions', metricsData.metrics.total_sessions || 0);
    setText('activeSessions', metricsData.metrics.active_sessions || 0);
    setText('avgDuration', metricsData.metrics.avg_duration_seconds 
      ? Math.round(metricsData.metrics.avg_duration_seconds) + 's' 
      : '0s');
    setText('avgMessages', metricsData.metrics.avg_messages_per_session 
      ? parseFloat(String(metricsData.metrics.avg_messages_per_session)).toFixed(1)
      : '0');
    
    // Load charts with optional date filters
    const distributionData = await api.getDurationDistribution(clientId, startDate, endDate ? endDate + 'T23:59:59' : undefined);
    renderDurationChart(distributionData.distribution);
    
    const messagesData = await api.getMessagesDistribution(clientId, startDate, endDate ? endDate + 'T23:59:59' : undefined);
    renderMessagesChart(messagesData.distribution);
    
    const timelineData = await api.getSessionTimeline(clientId, startDate, endDate ? endDate + 'T23:59:59' : undefined);
    renderDurationTimelineChart(timelineData.timeline);
    
    const productBubbleData = await api.getProductBubbleData(clientId);
    renderProductBubbleChart(productBubbleData.bubbles);
    
    // If date filters are provided, apply them to sessions table
    if (startDate || endDate) {
      sessionsState.startDate = startDate || null;
      sessionsState.endDate = endDate || null;
    }
    
    // Load paginated data
    await loadSessions(true);
    await loadNoProducts(true);
    await loadAlternatives(true);
    await loadProducts(true);
    await loadCategories(true);
    await loadOutOfStock(true);
    
    // Load marketing data if client is selected
    if (clientId) {
      await loadMarketingData();
    }
    
  } catch (error) {
    console.error('Error loading data:', error);
  }
}

// Sessions
async function loadSessions(reset: boolean = false): Promise<void> {
  if (reset) resetPaginatedState(sessionsState);
  
  const data = await api.getSessions({
    clientId: appState.selectedClientId,
    limit: sessionsState.limit,
    offset: sessionsState.offset,
    startDate: sessionsState.startDate || undefined,
    endDate: sessionsState.endDate ? sessionsState.endDate + 'T23:59:59' : undefined,
    sortBy: sessionsState.sortBy,
    sortOrder: sessionsState.sortOrder
  });
  
  updatePaginatedState(sessionsState, data.sessions, data.total, reset);
  renderSessions();
}

function renderSessions(): void {
  const container = $('sessionsTable');
  const countDiv = $('sessionsCount');
  const loadMoreBtn = $('sessionsLoadMore');
  
  if (countDiv) {
    countDiv.textContent = `Showing ${sessionsState.items.length} of ${sessionsState.total} sessions`;
  }
  
  if (!container) return;
  
  if (sessionsState.items.length === 0) {
    container.innerHTML = '<div class="loading">No sessions found</div>';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    return;
  }
  
  container.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>Session ID</th>
          <th style="display: none;">Client</th>
          <th>Started</th>
          <th>Duration</th>
          <th>Questions</th>
          <th>Answers</th>
          <th>Conversions</th>
          <th>Total Events</th>
        </tr>
      </thead>
      <tbody>
        ${sessionsState.items.map(session => `
          <tr style="cursor: pointer;" onclick="viewSession('${session.session_id}')">
            <td><code>${session.session_id}</code></td>
            <td style="display: none;">${session.client_id}</td>
            <td>${formatDateTime(session.started_at)}</td>
            <td>${Math.round(session.duration_seconds || 0)}s</td>
            <td>${session.questions_count || 0}</td>
            <td>${session.answers_count || 0}</td>
            <td>${session.conversions_count || 0}</td>
            <td>${session.event_count}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  if (loadMoreBtn) {
    loadMoreBtn.style.display = sessionsState.items.length < sessionsState.total ? 'block' : 'none';
  }
}

// No Products
async function loadNoProducts(reset: boolean = false): Promise<void> {
  if (reset) resetPaginatedState(noProductsState);
  
  const data = await api.getNoProducts({
    clientId: appState.selectedClientId,
    limit: noProductsState.limit,
    offset: noProductsState.offset,
    startDate: noProductsState.startDate || undefined,
    endDate: noProductsState.endDate ? noProductsState.endDate + 'T23:59:59' : undefined,
    searchQuery: noProductsState.searchQuery || undefined
  });
  
  updatePaginatedState(noProductsState, data.events, data.total, reset);
  renderNoProducts();
}

function renderNoProducts(): void {
  const container = $('noProductsTable');
  const countDiv = $('noProductsCount');
  const loadMoreBtn = $('noProductsLoadMore');
  
  if (countDiv) {
    countDiv.textContent = `Showing ${noProductsState.items.length} of ${noProductsState.total} events`;
  }
  
  if (!container) return;
  
  if (noProductsState.items.length === 0) {
    container.innerHTML = '<div class="loading">No events found</div>';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    return;
  }
  
  container.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Session</th>
          <th>Search Query</th>
        </tr>
      </thead>
      <tbody>
        ${noProductsState.items.map(event => `
          <tr style="cursor: pointer;" onclick="viewSession('${event.session_id}')">
            <td>${formatDateTime(event.created_at)}</td>
            <td><code style="font-size: 11px;">${event.session_id}</code></td>
            <td>${event.search_query || '-'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  if (loadMoreBtn) {
    loadMoreBtn.style.display = noProductsState.items.length < noProductsState.total ? 'block' : 'none';
  }
}

// Alternatives
async function loadAlternatives(reset: boolean = false): Promise<void> {
  if (reset) resetPaginatedState(alternativesState);
  
  const data = await api.getAlternatives({
    clientId: appState.selectedClientId,
    limit: alternativesState.limit,
    offset: alternativesState.offset,
    startDate: alternativesState.startDate || undefined,
    endDate: alternativesState.endDate ? alternativesState.endDate + 'T23:59:59' : undefined,
    searchedProduct: alternativesState.searchedProduct || undefined,
    suggestedProduct: alternativesState.suggestedProduct || undefined,
    category: alternativesState.category || undefined,
    sortBy: alternativesState.sortBy,
    sortOrder: alternativesState.sortOrder
  });
  
  updatePaginatedState(alternativesState, data.alternatives, data.total, reset);
  renderAlternatives();
}

function renderAlternatives(): void {
  const container = $('alternativesTable');
  const countDiv = $('alternativesCount');
  const loadMoreBtn = $('alternativesLoadMore');
  
  if (countDiv) {
    countDiv.textContent = `Showing ${alternativesState.items.length} of ${alternativesState.total} alternatives`;
  }
  
  if (!container) return;
  
  if (alternativesState.items.length === 0) {
    container.innerHTML = '<div class="loading">No alternatives found</div>';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    return;
  }
  
  container.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>Session ID</th>
          <th>User Searched For</th>
          <th>Suggested Alternative</th>
          <th>Category</th>
          <th>Price</th>
          <th>URL</th>
          <th>Alternatives in Session</th>
          <th>Timestamp</th>
        </tr>
      </thead>
      <tbody>
        ${alternativesState.items.map(alt => `
          <tr style="cursor: pointer;" onclick="viewSession('${alt.session_id}')">
            <td><code style="font-size: 11px;">${alt.session_id || '-'}</code></td>
            <td style="color: #e74c3c; font-weight: 600;">${alt.searched_product || '-'}</td>
            <td style="color: #27ae60; font-weight: 600;">${alt.product_name ? `<a href="${alt.product_url || '#'}" target="_blank" style="color: #27ae60;" onclick="event.stopPropagation();">${alt.product_name}</a>` : '-'}</td>
            <td>${alt.product_category || '-'}</td>
            <td>${alt.product_price ? formatPrice(alt.product_price) : '-'}</td>
            <td>${alt.product_url ? `<a href="${alt.product_url}" target="_blank" style="color: #3498db;" onclick="event.stopPropagation();">View</a>` : '-'}</td>
            <td style="text-align: center; font-weight: 600;">${alt.alternatives_count || '-'}</td>
            <td>${formatDateTime(alt.created_at)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  if (loadMoreBtn) {
    loadMoreBtn.style.display = alternativesState.items.length < alternativesState.total ? 'block' : 'none';
  }
}

// Products
async function loadProducts(reset: boolean = false): Promise<void> {
  if (reset) resetPaginatedState(productsState);
  
  const data = await api.getProducts({
    clientId: appState.selectedClientId,
    limit: productsState.limit,
    offset: productsState.offset,
    category: productsState.category || undefined,
    inStock: productsState.inStock ?? undefined,
    search: productsState.search || undefined,
    startDate: productsState.startDate || undefined,
    endDate: productsState.endDate ? productsState.endDate + 'T23:59:59' : undefined,
    sortBy: productsState.sortBy,
    sortOrder: productsState.sortOrder
  });
  
  updatePaginatedState(productsState, data.products, data.total, reset);
  renderProducts();
}

function renderProducts(): void {
  const container = $('productsTable');
  const countDiv = $('productsCount');
  const loadMoreBtn = $('productsLoadMore');
  
  if (countDiv) {
    countDiv.textContent = `Showing ${productsState.items.length} of ${productsState.total} products`;
  }
  
  if (!container) return;
  
  if (productsState.items.length === 0) {
    container.innerHTML = '<div class="loading">No products found</div>';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    return;
  }
  
  // Helper to render sort indicator
  const sortIndicator = (column: string) => {
    if (productsState.sortBy === column) {
      return productsState.sortOrder === 'DESC' ? ' ↓' : ' ↑';
    }
    return '';
  };
  
  // Clickable header style
  const thStyle = 'cursor: pointer; user-select: none;';
  
  container.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>ID</th>
          <th style="${thStyle}" onclick="sortProductsBy('product_name')">Product${sortIndicator('product_name')}</th>
          <th>SKU</th>
          <th>Category</th>
          <th style="${thStyle}" onclick="sortProductsBy('product_price')">Price${sortIndicator('product_price')}</th>
          <th style="${thStyle}" onclick="sortProductsBy('total_mentions')">Mentions${sortIndicator('total_mentions')}</th>
          <th style="${thStyle}" onclick="sortProductsBy('click_count')">Clicks${sortIndicator('click_count')}</th>
          <th style="${thStyle}" onclick="sortProductsBy('add_to_cart_count')">Add to Cart${sortIndicator('add_to_cart_count')}</th>
          <th style="${thStyle}" onclick="sortProductsBy('purchase_count')">Purchases${sortIndicator('purchase_count')}</th>
          <th>Real Stock</th>
        </tr>
      </thead>
      <tbody>
        ${productsState.items.map(product => `
          <tr>
            <td><code>${product.product_id || '-'}</code></td>
            <td>${product.product_name || '-'}</td>
            <td><code>${product.product_sku || '-'}</code></td>
            <td>${product.product_category || '-'}</td>
            <td>${product.product_price ? formatPrice(product.product_price) : '-'}</td>
            <td>${product.total_mentions || 0}</td>
            <td>${product.click_count || 0}</td>
            <td>${product.add_to_cart_count || 0}</td>
            <td>${product.purchase_count || 0}</td>
            <td id="stock-${product.product_id}" style="color: #999;">⏳ Loading...</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  if (loadMoreBtn) {
    loadMoreBtn.style.display = productsState.items.length < productsState.total ? 'block' : 'none';
  }
  
  // Fetch real-time stock asynchronously
  fetchRealTimeStock();
}

// Fetch real-time stock from WooCommerce
async function fetchRealTimeStock(): Promise<void> {
  const productIds = productsState.items
    .map(p => p.product_id?.toString())
    .filter(Boolean);
  
  if (productIds.length === 0) return;
  
  try {
    const { stock } = await api.getProductsStock(appState.selectedClientId, productIds);
    
    // Update each stock cell
    for (const product of productsState.items) {
      const stockCell = document.getElementById(`stock-${product.product_id}`);
      if (!stockCell) continue;
      
      const stockInfo = stock[product.product_id];
      if (stockInfo) {
        if (stockInfo.inStock) {
          const qty = stockInfo.stockQuantity !== null ? ` (${stockInfo.stockQuantity})` : '';
          stockCell.innerHTML = `✅ In Stock${qty}`;
          stockCell.style.color = '#27ae60';
        } else {
          stockCell.innerHTML = `❌ ${stockInfo.stockStatus === 'onbackorder' ? 'Backorder' : 'Out of Stock'}`;
          stockCell.style.color = '#e74c3c';
        }
      } else {
        stockCell.innerHTML = '❓ Unknown';
        stockCell.style.color = '#999';
      }
    }
  } catch (error) {
    console.error('Failed to fetch real-time stock:', error);
    // Mark all as error
    for (const product of productsState.items) {
      const stockCell = document.getElementById(`stock-${product.product_id}`);
      if (stockCell) {
        stockCell.innerHTML = '⚠️ Error';
        stockCell.style.color = '#f39c12';
      }
    }
  }
}

// Categories
async function loadCategories(reset: boolean = false): Promise<void> {
  if (reset) resetPaginatedState(categoriesState);
  
  const data = await api.getCategories({
    clientId: appState.selectedClientId,
    limit: categoriesState.limit,
    offset: categoriesState.offset,
    sortBy: categoriesState.sortBy,
    sortOrder: categoriesState.sortOrder
  });
  
  updatePaginatedState(categoriesState, data.categories, data.total, reset);
  renderCategories();
}

function renderCategories(): void {
  const container = $('categoriesTable');
  const loadMoreBtn = $('categoriesLoadMore');
  
  if (!container) return;
  
  if (categoriesState.items.length === 0) {
    container.innerHTML = '<div class="loading">No categories found</div>';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    return;
  }
  
  container.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>Category</th>
          <th>Products</th>
          <th>Sessions</th>
          <th>Total Mentions</th>
        </tr>
      </thead>
      <tbody>
        ${categoriesState.items.map(cat => `
          <tr>
            <td>${cat.category || 'Uncategorized'}</td>
            <td>${cat.product_count || 0}</td>
            <td>${cat.unique_sessions || 0}</td>
            <td>${cat.total_mentions || 0}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  if (loadMoreBtn) {
    loadMoreBtn.style.display = categoriesState.items.length < categoriesState.total ? 'block' : 'none';
  }
}

// Out of Stock
async function loadOutOfStock(reset: boolean = false): Promise<void> {
  if (reset) resetPaginatedState(outOfStockState);
  
  const data = await api.getOutOfStock({
    clientId: appState.selectedClientId,
    limit: outOfStockState.limit,
    offset: outOfStockState.offset
  });
  
  updatePaginatedState(outOfStockState, data.products, data.total, reset);
  renderOutOfStock();
}

function renderOutOfStock(): void {
  const container = $('outOfStockTable');
  const loadMoreBtn = $('outOfStockLoadMore');
  
  if (!container) return;
  
  if (outOfStockState.items.length === 0) {
    container.innerHTML = '<div class="loading">No out of stock products found</div>';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    return;
  }
  
  container.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>Product</th>
          <th>SKU</th>
          <th>Category</th>
          <th>Mentions</th>
          <th>Sessions</th>
          <th>Last Mentioned</th>
        </tr>
      </thead>
      <tbody>
        ${outOfStockState.items.map(product => `
          <tr>
            <td>${product.product_url ? `<a href="${product.product_url}" target="_blank">${product.product_name || '-'}</a>` : (product.product_name || '-')}</td>
            <td><code>${product.product_sku || '-'}</code></td>
            <td>${product.product_category || '-'}</td>
            <td>${product.mention_count || 0}</td>
            <td>${product.unique_sessions || 0}</td>
            <td>${product.last_mentioned ? formatDateTime(product.last_mentioned) : '-'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  if (loadMoreBtn) {
    loadMoreBtn.style.display = outOfStockState.items.length < outOfStockState.total ? 'block' : 'none';
  }
}

// Marketing Data
async function loadMarketingData(): Promise<void> {
  const clientId = appState.selectedClientId;
  if (!clientId) return;
  
  try {
    // Overview
    const overview = await api.getMarketingOverview(clientId);
    const orders = overview.orders || {} as any;
    const lift = overview.chatbotLift || {} as any;
    
    setText('miTotalOrders', orders.total || 0);
    setText('miDirectOrders', orders.direct || 0);
    setText('miAssistedOrders', orders.assisted || 0);
    setText('miOrganicOrders', orders.organic || 0);
    setText('miDirectPct', `${(lift.directPct || 0).toFixed(1)}%`);
    setText('miAssistedPct', `${(lift.assistedPct || 0).toFixed(1)}%`);
    setText('miOrganicPct', `${(lift.organicPct || 0).toFixed(1)}%`);
    setText('miTotalRevenue', formatPrice(orders.totalRevenue || 0));
    setText('miChatbotRevenue', formatPrice(orders.chatbotRevenue || 0));
    
    if (orders.lastSync) {
      setText('miSyncStatus', `Last sync: ${formatDateTime(orders.lastSync)}`);
    }
    
    // Conversion comparison
    const comparison = await api.getConversionComparison(clientId);
    const liftPct = comparison.lift?.percentage || 0;
    const liftElement = $('chatbotLiftPct');
    if (liftElement) {
      liftElement.textContent = liftPct > 0 ? `+${liftPct.toFixed(0)}%` : `${liftPct.toFixed(0)}%`;
      liftElement.style.color = liftPct > 0 ? '#27ae60' : liftPct < 0 ? '#e74c3c' : '#666';
    }
    setText('chatbotLiftMessage', comparison.lift?.message || 'No data yet');
    setText('chatbotCVR', `${(comparison.chatbot?.cvr || 0).toFixed(2)}%`);
    setText('chatbotSessions', `${comparison.chatbot?.sessions || 0} sessions`);
    setText('organicCVR', `${(comparison.organic?.cvr || 0).toFixed(2)}%`);
    setText('organicSessions', `${comparison.organic?.sessions || 0} sessions`);
    
    // Render the new comparison cards
    renderComparisonCards(comparison);
    
    // Funnel
    const funnelData = await api.getFunnel(clientId);
    renderFunnelChart(funnelData.funnel);
    
    // Traffic sources
    const sourcesData = await api.getTrafficSources(clientId);
    renderTrafficSourcesChart(sourcesData.sources);
    
    // Device types
    const devicesData = await api.getDeviceTypes(clientId);
    renderDeviceTypeChart(devicesData.devices);
    
    // Recommendations stats
    const recStats = await api.getRecommendationsStats(clientId);
    setText('recTotal', recStats.totalRecommendations || 0);
    setText('recUniqueProducts', recStats.uniqueProducts || 0);
    setText('recClickRate', `${(recStats.clickRate || 0).toFixed(1)}%`);
    setText('recCartRate', `${(recStats.cartRate || 0).toFixed(1)}%`);
    
    // Top recommendations
    const topRec = await api.getTopRecommendations(clientId, 10);
    renderTopRecommendationsTable(topRec.recommendations);
    
    // Orders
    await loadOrders(true);
    
  } catch (error) {
    console.error('Error loading marketing data:', error);
  }
}

// Orders
async function loadOrders(reset: boolean = false): Promise<void> {
  const clientId = appState.selectedClientId;
  if (!clientId) return;
  
  if (reset) resetPaginatedState(ordersState);
  
  const data = await api.getOrders({
    clientId,
    limit: ordersState.limit,
    offset: ordersState.offset,
    startDate: ordersState.startDate || undefined,
    endDate: ordersState.endDate ? ordersState.endDate + 'T23:59:59' : undefined,
    attribution: ordersState.attribution || undefined,
    orderId: ordersState.orderId || undefined,
    sortBy: ordersState.sortBy,
    sortOrder: ordersState.sortOrder
  });
  
  updatePaginatedState(ordersState, data.orders, data.total, reset);
  renderOrders();
}

function renderOrders(): void {
  const container = $('ordersTable');
  const countDiv = $('ordersCount');
  const loadMoreBtn = $('ordersLoadMore');
  
  if (countDiv) {
    countDiv.textContent = `Showing ${ordersState.items.length} of ${ordersState.total} orders`;
  }
  
  if (!container) return;
  
  if (ordersState.items.length === 0) {
    container.innerHTML = '<div class="loading">No orders found</div>';
    if (loadMoreBtn) loadMoreBtn.style.display = 'none';
    return;
  }
  
  const attributionBadge = (type: string) => {
    const badges: Record<string, string> = {
      'chatbot_direct': '<span style="background: #667eea; color: white; padding: 2px 8px; border-radius: 12px; font-size: 11px;">💬 Direct</span>',
      'chatbot_assisted': '<span style="background: #764ba2; color: white; padding: 2px 8px; border-radius: 12px; font-size: 11px;">🤝 Assisted</span>',
      'organic': '<span style="background: #95a5a6; color: white; padding: 2px 8px; border-radius: 12px; font-size: 11px;">🌐 Organic</span>'
    };
    return badges[type] || type;
  };
  
  container.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>Order</th>
          <th>Date</th>
          <th>Total</th>
          <th>Attribution</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        ${ordersState.items.map(order => `
          <tr>
            <td><strong>#${order.order_number || order.order_id}</strong></td>
            <td>${formatDateTime(order.order_date)}</td>
            <td style="font-weight: 600; color: #27ae60;">${formatPrice(order.total)}</td>
            <td>${attributionBadge(order.attribution_type)}</td>
            <td>
              <button onclick="viewOrderDetails(${order.order_id})" style="padding: 4px 12px; background: #3498db; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; margin-right: 5px;">View</button>
              <button onclick="deleteOrder(${order.order_id})" style="padding: 4px 12px; background: #e74c3c; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;">Delete</button>
            </td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
  
  if (loadMoreBtn) {
    loadMoreBtn.style.display = ordersState.items.length < ordersState.total ? 'block' : 'none';
  }
}

// Render comparison cards for Chatbot vs Organic Performance
function renderComparisonCards(comparison: any): void {
  const chatbot = comparison.chatbot || {};
  const organic = comparison.organic || {};
  
  // Chatbot metrics
  setText('compChatbotSessions', formatNumber(chatbot.sessions || 0));
  setText('compChatbotOrders', formatNumber(chatbot.conversions || 0));
  setText('compChatbotCVR', `${(chatbot.cvr || 0).toFixed(2)}%`);
  setText('compChatbotRevenue', formatPrice(chatbot.revenue || 0));
  setText('compChatbotAOV', formatPrice(chatbot.aov || 0));
  
  // Organic metrics (no sessions/CVR - we only track chatbot users)
  setText('compOrganicOrders', formatNumber(organic.conversions || 0));
  setText('compOrganicRevenue', formatPrice(organic.revenue || 0));
  setText('compOrganicAOV', formatPrice(organic.aov || 0));
}

// Format number with thousands separator
function formatNumber(num: number): string {
  return new Intl.NumberFormat('de-DE').format(num);
}

function renderTopRecommendationsTable(recommendations: any[]): void {
  const container = $('topRecommendationsTable');
  if (!container) return;
  
  if (!recommendations || recommendations.length === 0) {
    container.innerHTML = '<div class="loading">No recommendations data</div>';
    return;
  }
  
  container.innerHTML = `
    <table>
      <thead>
        <tr>
          <th>Product</th>
          <th>Shown</th>
          <th>Clicked</th>
          <th>Added to Cart</th>
          <th>Purchased</th>
        </tr>
      </thead>
      <tbody>
        ${recommendations.map(rec => `
          <tr>
            <td>${rec.product_name || rec.product_id}</td>
            <td>${rec.times_recommended || 0}</td>
            <td>${rec.times_clicked || 0}</td>
            <td>${rec.times_added_to_cart || 0}</td>
            <td>${rec.times_purchased || 0}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

// View session details
async function viewSession(sessionId: string): Promise<void> {
  try {
    const data = await api.getSessionDetails(sessionId);
    
    const modal = $('sessionModal');
    const modalSessionId = $('modalSessionId');
    const modalContent = $('modalContent');
    
    if (!modal || !modalContent) return;
    
    if (modalSessionId) {
      modalSessionId.textContent = `Session: ${sessionId}`;
    }
    
    const events = data.events || [];
    const started = events[0] ? formatDateTime(events[0].timestamp) : 'Unknown';
    
    // Calculate duration from first to last event
    let duration = '0s';
    if (events.length >= 2) {
      const firstTime = new Date(events[0].timestamp).getTime();
      const lastTime = new Date(events[events.length - 1].timestamp).getTime();
      const seconds = Math.round((lastTime - firstTime) / 1000);
      duration = seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    }
    
    modalContent.innerHTML = `
      <div style="margin-bottom: 20px; padding: 15px; background: #f8f9fa; border-radius: 8px;">
        <p style="margin: 5px 0;"><strong>Total Events:</strong> ${events.length}</p>
        <p style="margin: 5px 0;"><strong>Started:</strong> ${started}</p>
        <p style="margin: 5px 0;"><strong>Duration:</strong> ${duration}</p>
      </div>
      ${events.map((event: any) => {
        let icon = '📌';
        let label = event.event_type;
        let content = '';
        let messageClass = 'assistant';
        
        if (event.event_type === 'message_sent') {
          icon = '👤';
          label = 'User';
          content = event.data?.message || event.data?.content || '';
          messageClass = 'user';
        } else if (event.event_type === 'message_received') {
          icon = '🤖';
          label = 'Assistant';
          content = event.data?.message || event.data?.content || '';
          messageClass = 'assistant';
        } else if (event.event_type === 'widget_opened') {
          icon = '🚀';
          label = 'Widget Opened';
          content = 'User opened the chatbot';
          messageClass = 'assistant';
        } else if (event.event_type === 'widget_closed') {
          icon = '👋';
          label = 'Widget Closed';
          content = 'User closed the chatbot';
          messageClass = 'assistant';
        } else if (event.event_type === 'products_recommended') {
          icon = '🎁';
          label = 'Products Recommended';
          content = event.data?.products ? `Recommended ${event.data.products.length} products` : 'Products recommended';
          messageClass = 'assistant';
        }
        
        // Skip events with no content (just show chat messages and important events)
        if (!content && event.event_type !== 'widget_opened' && event.event_type !== 'widget_closed') {
          return '';
        }
        
        return `
          <div class="message ${messageClass}">
            <div class="message-avatar">${icon}</div>
            <div class="message-content">
              <div class="message-bubble">${content}</div>
              <div class="message-time">${formatDateTime(event.timestamp)}</div>
            </div>
          </div>
        `;
      }).filter(Boolean).join('')}
    `;
    
    modal.style.display = 'block';
  } catch (error) {
    console.error('Error loading session details:', error);
    alert('Failed to load session details');
  }
}

// View order details
async function viewOrderDetails(orderId: number): Promise<void> {
  try {
    const data = await api.getOrderDetails(String(orderId), appState.selectedClientId);
    const order = data.order;
    const items = data.items || [];
    const coupons = data.coupons || [];
    
    const modal = $('sessionModal');
    const modalSessionId = $('modalSessionId');
    const modalContent = $('modalContent');
    
    if (!modal || !modalContent) return;
    
    if (modalSessionId) {
      modalSessionId.textContent = `Order #${orderId}`;
    }
    
    modalContent.innerHTML = `
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
        <div>
          <h3>Order Info</h3>
          <p><strong>Date:</strong> ${formatDateTime(order.order_date)}</p>
          <p><strong>Status:</strong> ${order.order_status}</p>
          <p><strong>Total:</strong> ${formatPrice(order.total)}</p>
          <p><strong>Attribution:</strong> ${order.attribution_type}</p>
          ${order.session_id ? `<p><strong>Session:</strong> <a href="#" onclick="viewSessionTimeline('${order.session_id}'); return false;">${order.session_id}</a></p>` : ''}
        </div>
        <div>
          <h3>Customer</h3>
          <p><strong>Email:</strong> ${order.customer_email || 'Guest'}</p>
          <p><strong>Name:</strong> ${order.customer_first_name || ''} ${order.customer_last_name || ''}</p>
        </div>
      </div>
      <h3 style="margin-top: 20px;">Items</h3>
      <table>
        <thead>
          <tr>
            <th>Product</th>
            <th>SKU</th>
            <th>Qty</th>
            <th>Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          ${items.map((item: any) => `
            <tr>
              <td>${item.product_name}</td>
              <td><code>${item.product_sku || '-'}</code></td>
              <td>${item.quantity}</td>
              <td>${formatPrice(item.price)}</td>
              <td>${formatPrice(item.line_total || item.price * item.quantity)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      ${coupons.length ? `
        <h3 style="margin-top: 20px;">Coupons</h3>
        <p>${coupons.map((c: any) => `<code>${c.coupon_code}</code> (-${formatPrice(c.discount_amount)})`).join(', ')}</p>
      ` : ''}
    `;
    
    modal.style.display = 'block';
  } catch (error) {
    console.error('Error loading order details:', error);
    alert('Failed to load order details');
  }
}

// Sync orders
async function syncOrders(): Promise<void> {
  const clientId = appState.selectedClientId;
  if (!clientId) {
    alert('Please select a client first');
    return;
  }
  
  const btn = $('syncOrdersBtn') as HTMLButtonElement;
  if (btn) {
    btn.disabled = true;
    btn.textContent = '⏳ Syncing...';
  }
  
  try {
    const result = await api.syncOrders(clientId);
    
    if (result.success) {
      const count = result.stats?.ordersProcessed || 0;
      alert(`Sync complete! Synced ${count} orders.`);
      loadData();
    } else {
      alert(`Sync failed: ${result.error}`);
    }
  } catch (error: any) {
    console.error('Sync error:', error);
    alert('Sync failed: ' + error.message);
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.textContent = '🔄 Sync WooCommerce Orders';
    }
  }
}

// Delete order
async function deleteOrder(orderId: number): Promise<void> {
  const clientId = appState.selectedClientId;
  if (!clientId) {
    alert('Please select a client first');
    return;
  }
  
  if (!confirm(`Are you sure you want to delete order #${orderId}? This cannot be undone.`)) {
    return;
  }
  
  try {
    const result = await api.deleteOrder(orderId, clientId);
    
    if (result.success) {
      alert(`Order #${orderId} deleted successfully.`);
      await loadOrders(true); // Reload orders table
      await loadMarketingData(); // Refresh marketing stats
    } else {
      alert(`Delete failed: ${result.error}`);
    }
  } catch (error: any) {
    console.error('Delete error:', error);
    alert('Delete failed: ' + error.message);
  }
}

// Close modal
function closeModal(): void {
  const modal = $('sessionModal');
  if (modal) modal.style.display = 'none';
}

// ============================================
// AUTHENTICATION FUNCTIONS
// ============================================

function showLoginScreen(): void {
  const loginScreen = $('loginScreen');
  const dashboardScreen = $('dashboardScreen');
  
  if (loginScreen) loginScreen.style.display = 'flex';
  if (dashboardScreen) dashboardScreen.style.display = 'none';
}

function showDashboard(): void {
  const loginScreen = $('loginScreen');
  const dashboardScreen = $('dashboardScreen');
  
  if (loginScreen) loginScreen.style.display = 'none';
  if (dashboardScreen) dashboardScreen.style.display = 'block';
}

async function handleLogin(): Promise<void> {
  const usernameInput = $('loginUsername') as HTMLInputElement;
  const passwordInput = $('loginPassword') as HTMLInputElement;
  const errorDiv = $('loginError');
  const submitBtn = $('loginSubmit') as HTMLButtonElement;
  
  const username = usernameInput?.value?.trim();
  const password = passwordInput?.value;
  
  if (!username || !password) {
    if (errorDiv) errorDiv.textContent = 'Please enter username and password';
    return;
  }
  
  // Disable button during login
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.textContent = 'Logging in...';
  }
  if (errorDiv) errorDiv.textContent = '';
  
  try {
    const result = await api.login(username, password);
    
    if (result.success) {
      showDashboard();
      await initDashboard();
    } else {
      if (errorDiv) errorDiv.textContent = result.error || 'Login failed';
    }
  } catch (error: any) {
    if (errorDiv) errorDiv.textContent = error.message || 'Network error';
  } finally {
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Login';
    }
  }
}

function handleLogout(): void {
  api.logout();
  showLoginScreen();
  
  // Clear password field
  const passwordInput = $('loginPassword') as HTMLInputElement;
  if (passwordInput) passwordInput.value = '';
}

// Filter functions
function applySessionsFilter(): void {
  sessionsState.startDate = parseEuroDate(($('sessionsStartDate') as HTMLInputElement)?.value) || null;
  sessionsState.endDate = parseEuroDate(($('sessionsEndDate') as HTMLInputElement)?.value) || null;
  sessionsState.sortBy = ($('sessionsSortBy') as HTMLSelectElement)?.value || 'started_at';
  sessionsState.sortOrder = (($('sessionsSortOrder') as HTMLSelectElement)?.value || 'DESC') as 'ASC' | 'DESC';
  loadSessions(true);
}

function resetSessionsFilter(): void {
  const startDate = $('sessionsStartDate') as HTMLInputElement;
  const endDate = $('sessionsEndDate') as HTMLInputElement;
  const sortBy = $('sessionsSortBy') as HTMLSelectElement;
  const sortOrder = $('sessionsSortOrder') as HTMLSelectElement;
  
  if (startDate) startDate.value = '';
  if (endDate) endDate.value = '';
  if (sortBy) sortBy.value = 'started_at';
  if (sortOrder) sortOrder.value = 'DESC';
  
  sessionsState.startDate = null;
  sessionsState.endDate = null;
  sessionsState.sortBy = 'started_at';
  sessionsState.sortOrder = 'DESC';
  loadSessions(true);
}

// Initialize dashboard after authentication
async function initDashboard(): Promise<void> {
  // Load clients first - must complete before connecting live stream
  await loadClients();
  
  // Initialize date pickers
  initDatePickers();
  
  // Initialize session explorer
  initSessionExplorer();
  
  // Set up client filter listener
  const clientFilter = $('clientFilter') as HTMLSelectElement;
  if (clientFilter) {
    clientFilter.addEventListener('change', () => {
      appState.selectedClientId = clientFilter.value;
      loadData();
      
      // Reconnect live stream if on live tab
      if (appState.currentTab === 'live') {
        connectLiveStream();
      }
    });
  }
  
  // Set up modal close
  const modal = $('sessionModal');
  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
  }
  
  const closeBtn = document.querySelector('.close');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeModal);
  }
  
  // Load product categories for filter dropdown
  await loadProductCategories();
  
  loadData();
  
  // Auto-refresh
  appState.refreshInterval = setInterval(() => {
    if (appState.currentTab !== 'live') {
      // Use stored filters for auto-refresh
      const { startDate, endDate } = appState.sessionIntelligenceFilters;
      loadData(startDate || undefined, endDate || undefined);
    }
  }, appState.refreshRate);
}

// Main init function - check auth first
async function init(): Promise<void> {
  // Set up login form handlers
  const loginForm = $('loginForm');
  if (loginForm) {
    loginForm.addEventListener('submit', (e) => {
      e.preventDefault();
      handleLogin();
    });
  }
  
  // Set up logout button
  const logoutBtn = $('logoutBtn');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', handleLogout);
  }
  
  // Listen for logout events (from API 401 responses)
  window.addEventListener('auth:logout', () => {
    showLoginScreen();
  });
  
  // Check if already authenticated
  const authResult = await api.verifyToken();
  
  if (authResult.valid) {
    showDashboard();
    await initDashboard();
  } else {
    showLoginScreen();
  }
}

// Expose functions globally for onclick handlers
(window as any).switchTab = switchTab;
(window as any).loadData = loadData;
(window as any).loadSessionExplorer = loadSessionExplorer;
(window as any).viewSession = viewSession;
(window as any).viewOrderDetails = viewOrderDetails;
(window as any).syncOrders = syncOrders;
(window as any).deleteOrder = deleteOrder;
(window as any).closeModal = closeModal;
(window as any).handleLogin = handleLogin;
(window as any).handleLogout = handleLogout;
(window as any).applySessionIntelligenceFilter = applySessionIntelligenceFilter;
(window as any).resetSessionIntelligenceFilter = resetSessionIntelligenceFilter;
(window as any).applySessionsFilter = applySessionsFilter;
(window as any).applySessionsSort = applySessionsSort;
(window as any).resetSessionsFilter = resetSessionsFilter;
(window as any).applyProductsFilter = applyProductsFilter;
(window as any).resetProductsFilter = resetProductsFilter;
(window as any).sortProductsBy = sortProductsBy;
(window as any).applyAlternativesFilter = applyAlternativesFilter;
(window as any).resetAlternativesFilter = resetAlternativesFilter;
(window as any).loadMoreSessions = () => loadSessions(false);
(window as any).loadMoreNoProducts = () => loadNoProducts(false);
(window as any).loadMoreAlternatives = () => loadAlternatives(false);
(window as any).loadMoreProducts = () => loadProducts(false);
(window as any).loadMoreCategories = () => loadCategories(false);
(window as any).loadMoreOutOfStock = () => loadOutOfStock(false);
(window as any).loadMoreOrders = () => loadOrders(false);

// Session Intelligence Filter Functions
async function applySessionIntelligenceFilter(): Promise<void> {
  const startDateInput = ($('sessionIntelStartDate') as HTMLInputElement)?.value;
  const endDateInput = ($('sessionIntelEndDate') as HTMLInputElement)?.value;
  
  const startDate = startDateInput ? parseEuroDate(startDateInput) : undefined;
  const endDate = endDateInput ? parseEuroDate(endDateInput) : undefined;
  
  // Store filters in app state for auto-refresh
  appState.sessionIntelligenceFilters.startDate = startDate || null;
  appState.sessionIntelligenceFilters.endDate = endDate || null;
  
  // Update filter info display
  const filterInfo = $('sessionIntelFilterInfo');
  if (filterInfo) {
    if (startDate || endDate) {
      const parts = [];
      if (startDate) parts.push(`From: ${startDateInput}`);
      if (endDate) parts.push(`To: ${endDateInput}`);
      filterInfo.textContent = `📊 Filtered: ${parts.join(' | ')}`;
      filterInfo.style.color = '#3498db';
      filterInfo.style.fontWeight = '600';
    } else {
      filterInfo.textContent = '';
    }
  }
  
  // Reload all session intelligence data with filters
  await loadData(startDate, endDate);
}

async function resetSessionIntelligenceFilter(): Promise<void> {
  const startDateInput = $('sessionIntelStartDate') as HTMLInputElement;
  const endDateInput = $('sessionIntelEndDate') as HTMLInputElement;
  const filterInfo = $('sessionIntelFilterInfo');
  
  if (startDateInput) startDateInput.value = '';
  if (endDateInput) endDateInput.value = '';
  if (filterInfo) filterInfo.textContent = '';
  
  // Clear filters from app state
  appState.sessionIntelligenceFilters.startDate = null;
  appState.sessionIntelligenceFilters.endDate = null;
  
  // Reload all data without filters
  await loadData();
}

// Sessions Table Sort Functions (separate from date filters)
async function applySessionsSort(): Promise<void> {
  const sortBy = ($('sessionsSortBy') as HTMLSelectElement)?.value || 'started_at';
  const sortOrder = ($('sessionsSortOrder') as HTMLSelectElement)?.value || 'DESC';
  
  sessionsState.sortBy = sortBy;
  sessionsState.sortOrder = sortOrder as 'ASC' | 'DESC';
  
  await loadSessions(true);
}

// Products Table Filter & Sort Functions
async function applyProductsFilter(): Promise<void> {
  const searchInput = ($('productsSearch') as HTMLInputElement)?.value;
  const categoryInput = ($('productsCategoryFilter') as HTMLSelectElement)?.value;
  const startDateInput = ($('productsStartDate') as HTMLInputElement)?.value;
  const endDateInput = ($('productsEndDate') as HTMLInputElement)?.value;
  const sortByInput = ($('productsSortBy') as HTMLSelectElement)?.value;
  const sortOrderInput = ($('productsSortOrder') as HTMLSelectElement)?.value;
  
  // Update state
  productsState.search = searchInput || null;
  productsState.category = categoryInput || null;
  productsState.startDate = startDateInput ? parseEuroDate(startDateInput) : null;
  productsState.endDate = endDateInput ? parseEuroDate(endDateInput) : null;
  productsState.sortBy = sortByInput || 'total_mentions';
  productsState.sortOrder = (sortOrderInput || 'DESC') as 'ASC' | 'DESC';
  
  // Update filter info display
  const filterInfo = $('productsFilterInfo');
  if (filterInfo) {
    const parts = [];
    if (searchInput) parts.push(`Search: "${searchInput}"`);
    if (categoryInput) parts.push(`Category: ${categoryInput}`);
    if (startDateInput) parts.push(`From: ${startDateInput}`);
    if (endDateInput) parts.push(`To: ${endDateInput}`);
    
    if (parts.length > 0) {
      filterInfo.textContent = `🔍 Filtered: ${parts.join(' | ')}`;
    } else {
      filterInfo.textContent = '';
    }
  }
  
  // Reload with new filters (reset pagination)
  await loadProducts(true);
}

async function resetProductsFilter(): Promise<void> {
  // Clear inputs
  const searchInput = $('productsSearch') as HTMLInputElement;
  const categoryInput = $('productsCategoryFilter') as HTMLSelectElement;
  const startDateInput = $('productsStartDate') as HTMLInputElement;
  const endDateInput = $('productsEndDate') as HTMLInputElement;
  const sortByInput = $('productsSortBy') as HTMLSelectElement;
  const sortOrderInput = $('productsSortOrder') as HTMLSelectElement;
  const filterInfo = $('productsFilterInfo');
  
  if (searchInput) searchInput.value = '';
  if (categoryInput) categoryInput.value = '';
  if (startDateInput) startDateInput.value = '';
  if (endDateInput) endDateInput.value = '';
  if (sortByInput) sortByInput.value = 'total_mentions';
  if (sortOrderInput) sortOrderInput.value = 'DESC';
  if (filterInfo) filterInfo.textContent = '';
  
  // Reset state
  productsState.search = null;
  productsState.category = null;
  productsState.startDate = null;
  productsState.endDate = null;
  productsState.sortBy = 'total_mentions';
  productsState.sortOrder = 'DESC';
  
  // Reload
  await loadProducts(true);
}

// Load unique categories for the product filter dropdown
async function loadProductCategories(): Promise<void> {
  try {
    const data = await api.getCategories({
      clientId: appState.selectedClientId,
      limit: 100,
      offset: 0
    });
    
    const select = $('productsCategoryFilter') as HTMLSelectElement;
    if (!select) return;
    
    // Keep the "All Categories" option
    select.innerHTML = '<option value="">All Categories</option>';
    
    // Add categories sorted by name
    const sortedCategories = data.categories
      .filter((cat: any) => cat.category)
      .sort((a: any, b: any) => (a.category || '').localeCompare(b.category || ''));
    
    sortedCategories.forEach((cat: any) => {
      const option = document.createElement('option');
      option.value = cat.category;
      option.textContent = `${cat.category} (${cat.total_mentions || 0})`;
      select.appendChild(option);
    });
  } catch (error) {
    console.error('Error loading product categories:', error);
  }
}

// Sort products by clicking column header (toggles ASC/DESC)
async function sortProductsBy(column: string): Promise<void> {
  // Toggle sort order if same column, otherwise default to DESC
  if (productsState.sortBy === column) {
    productsState.sortOrder = productsState.sortOrder === 'DESC' ? 'ASC' : 'DESC';
  } else {
    productsState.sortBy = column;
    productsState.sortOrder = 'DESC';
  }
  
  // Update dropdown to match
  const sortByInput = $('productsSortBy') as HTMLSelectElement;
  const sortOrderInput = $('productsSortOrder') as HTMLSelectElement;
  if (sortByInput) sortByInput.value = column;
  if (sortOrderInput) sortOrderInput.value = productsState.sortOrder;
  
  // Reload with new sort
  await loadProducts(true);
}

// Alternatives Filter & Sort Functions
async function applyAlternativesFilter(): Promise<void> {
  const startDateInput = ($('alternativesStartDate') as HTMLInputElement)?.value;
  const endDateInput = ($('alternativesEndDate') as HTMLInputElement)?.value;
  const searchedProductInput = ($('alternativesSearchedProduct') as HTMLInputElement)?.value;
  const suggestedProductInput = ($('alternativesSuggestedProduct') as HTMLInputElement)?.value;
  const categoryInput = ($('alternativesCategory') as HTMLInputElement)?.value;
  const sortByInput = ($('alternativesSortBy') as HTMLSelectElement)?.value;
  const sortOrderInput = ($('alternativesSortOrder') as HTMLSelectElement)?.value;
  
  // Update state
  alternativesState.startDate = startDateInput ? parseEuroDate(startDateInput) : null;
  alternativesState.endDate = endDateInput ? parseEuroDate(endDateInput) : null;
  alternativesState.searchedProduct = searchedProductInput || null;
  alternativesState.suggestedProduct = suggestedProductInput || null;
  alternativesState.category = categoryInput || null;
  alternativesState.sortBy = sortByInput || 'created_at';
  alternativesState.sortOrder = (sortOrderInput || 'DESC') as 'ASC' | 'DESC';
  
  // Update filter info display
  const filterInfo = $('alternativesFilterInfo');
  if (filterInfo) {
    const parts = [];
    if (searchedProductInput) parts.push(`Searched: "${searchedProductInput}"`);
    if (suggestedProductInput) parts.push(`Suggested: "${suggestedProductInput}"`);
    if (categoryInput) parts.push(`Category: ${categoryInput}`);
    if (startDateInput) parts.push(`From: ${startDateInput}`);
    if (endDateInput) parts.push(`To: ${endDateInput}`);
    
    if (parts.length > 0) {
      filterInfo.textContent = `🔍 Filtered: ${parts.join(' | ')}`;
    } else {
      filterInfo.textContent = '';
    }
  }
  
  // Reload with new filters (reset pagination)
  await loadAlternatives(true);
}

async function resetAlternativesFilter(): Promise<void> {
  // Clear inputs
  const startDateInput = $('alternativesStartDate') as HTMLInputElement;
  const endDateInput = $('alternativesEndDate') as HTMLInputElement;
  const searchedProductInput = $('alternativesSearchedProduct') as HTMLInputElement;
  const suggestedProductInput = $('alternativesSuggestedProduct') as HTMLInputElement;
  const categoryInput = $('alternativesCategory') as HTMLInputElement;
  const sortByInput = $('alternativesSortBy') as HTMLSelectElement;
  const sortOrderInput = $('alternativesSortOrder') as HTMLSelectElement;
  const filterInfo = $('alternativesFilterInfo');
  
  if (startDateInput) startDateInput.value = '';
  if (endDateInput) endDateInput.value = '';
  if (searchedProductInput) searchedProductInput.value = '';
  if (suggestedProductInput) suggestedProductInput.value = '';
  if (categoryInput) categoryInput.value = '';
  if (sortByInput) sortByInput.value = 'created_at';
  if (sortOrderInput) sortOrderInput.value = 'DESC';
  if (filterInfo) filterInfo.textContent = '';
  
  // Reset state
  alternativesState.startDate = null;
  alternativesState.endDate = null;
  alternativesState.searchedProduct = null;
  alternativesState.suggestedProduct = null;
  alternativesState.category = null;
  alternativesState.sortBy = 'created_at';
  alternativesState.sortOrder = 'DESC';
  
  // Reload
  await loadAlternatives(true);
}

// Start when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
